import { useMemo } from 'react';
import { useQueries } from 'react-query';
import luminaireAPI from '../../../luminaire/service/luminaireAPI';
import { LuminaireMetricFilter } from '../../../luminaire/types/luminaire.types';
import { Point } from '../../../Jouly/types/types';
import { ElementAction, GridElement, Light } from '../types';
import { LuminaireLiveMetric, LuminaireLiveMetricsMap } from '../types/presenter.types';

const OFFLINE_THRESHOLD_MS = 24 * 60 * 60 * 1000;
const REFETCH_INTERVAL_MS = 60_000;
// Hors-ligne: refetch lent au lieu de toutes les 60s (cache long).
const OFFLINE_REFETCH_INTERVAL_MS = 10 * 60 * 1000;
const STALE_TIME_MS = 5 * 60 * 1000;
const CACHE_TIME_MS = 30 * 60 * 1000;

const refetchByStatus = (data?: { lastSeenMs: number | null }): number => {
  const online =
    !!data && data.lastSeenMs !== null && Date.now() - data.lastSeenMs < OFFLINE_THRESHOLD_MS;
  return online ? REFETCH_INTERVAL_MS : OFFLINE_REFETCH_INTERVAL_MS;
};

const toFiniteNumber = (value: unknown): number | null => {
  const numericValue = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numericValue)) return null;
  return numericValue;
};

const getPointValue = (point?: Point): number | null => {
  if (!point) return null;
  const direct = toFiniteNumber(point.Value);
  if (direct !== null) return direct;
  return toFiniteNumber(point.Values?._value);
};

const getPointTimeMs = (point?: Point): number | null => {
  if (!point) return null;
  const raw = point.Time || point.Values?._time;
  if (!raw) return null;
  const ms = Date.parse(raw);
  return Number.isFinite(ms) ? ms : null;
};

const getLastValue = (points: Point[] | undefined): number | null => {
  if (!points || points.length === 0) return null;
  for (let i = points.length - 1; i >= 0; i -= 1) {
    const v = getPointValue(points[i]);
    if (v !== null) return v;
  }
  return null;
};

const getLastPointTimeMs = (points: Point[] | undefined): number | null => {
  if (!points || points.length === 0) return null;
  for (let i = points.length - 1; i >= 0; i -= 1) {
    const t = getPointTimeMs(points[i]);
    if (t !== null) return t;
  }
  return null;
};

const isoDate = (offsetDays = 0): string => {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
};

interface LightTarget {
  lightId: string;
  devEui: string;
}

interface RawResult {
  power: number | null;
  rssi: number | null;
  lastSeenMs: number | null;
}

const fetchLuminaireLive = async (devEui: string): Promise<RawResult> => {
  const filter: LuminaireMetricFilter = {
    devEui,
    startDate: isoDate(-1),
    stopDate: isoDate(0),
    aggregateWindow: '1h',
  };

  const [powerRes, rssiRes] = await Promise.allSettled([
    luminaireAPI.getLuminaireMetric('active_power', filter),
    luminaireAPI.getLuminaireMetric('downlink_rssi', filter),
  ]);

  const power = powerRes.status === 'fulfilled' ? getLastValue(powerRes.value) : null;
  const rssi = rssiRes.status === 'fulfilled' ? getLastValue(rssiRes.value) : null;

  const powerTime = powerRes.status === 'fulfilled' ? getLastPointTimeMs(powerRes.value) : null;
  const rssiTime = rssiRes.status === 'fulfilled' ? getLastPointTimeMs(rssiRes.value) : null;
  const lastSeenMs =
    powerTime !== null && rssiTime !== null
      ? Math.max(powerTime, rssiTime)
      : powerTime ?? rssiTime;

  return { power, rssi, lastSeenMs };
};

export interface UseAllLuminairesLiveMetricsResult {
  metrics: LuminaireLiveMetricsMap;
  isLoading: boolean;
}

export const useAllLuminairesLiveMetrics = (
  elements: GridElement[],
  enabled: boolean,
  visibleIds?: Set<string> | null,
): UseAllLuminairesLiveMetricsResult => {
  const targets: LightTarget[] = useMemo(() => {
    const result: LightTarget[] = [];
    elements.forEach((el) => {
      if (el.type !== 'pole' || !el.lights) return;
      el.lights.forEach((light: Light) => {
        if (light.action === ElementAction.TO_DELETE) return;
        const devEui = light.zhagaDevui?.trim();
        if (!devEui) return;
        result.push({ lightId: light.id, devEui });
      });
    });
    return result;
  }, [elements]);

  const queries = useQueries(
    targets.map((t) => ({
      queryKey: ['plan-bt-v2-all-luminaires-live', t.devEui],
      queryFn: () => fetchLuminaireLive(t.devEui),
      // Seulement les luminaires affichés individuellement (hors cluster, dans la vue).
      enabled: enabled && Boolean(t.devEui) && !!visibleIds && visibleIds.has(t.lightId),
      refetchInterval: (data?: RawResult) => refetchByStatus(data),
      staleTime: STALE_TIME_MS,
      cacheTime: CACHE_TIME_MS,
    })),
  );

  const dataUpdatedSignature = queries.map((q) => q.dataUpdatedAt).join(',');

  const metrics: LuminaireLiveMetricsMap = useMemo(() => {
    const now = Date.now();
    const map: LuminaireLiveMetricsMap = new Map();
    targets.forEach((t, idx) => {
      const data = queries[idx]?.data as RawResult | undefined;
      const lastSeenMs = data?.lastSeenMs ?? null;
      const isOnline = lastSeenMs !== null && now - lastSeenMs < OFFLINE_THRESHOLD_MS;
      const entry: LuminaireLiveMetric = {
        power: data?.power ?? null,
        rssi: data?.rssi ?? null,
        lastSeenMs,
        isOnline,
      };
      map.set(t.lightId, entry);
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targets, dataUpdatedSignature]);

  const isLoading = queries.some((q) => q.isLoading);

  return { metrics, isLoading };
};

export default useAllLuminairesLiveMetrics;
